import { CardEnum, PlayerColor } from '@colonist/game-states';
import { TradeEmbargoControllerReader } from '@colonist/game-base-types';
import { TradeOfferOpponentDisplay } from './TradeTypes.ts';
import { TradeUtils } from './TradeUtils.ts';
import { ArrayUtils, RandomUtils, TimeUtils, GameTimerUtils } from '@colonist/utils';
export class TradeValidator {
    /**
     * About tradeExecutorColor:
    *  * Returning the first element of the current turn player colors array is a bad practice.
    *  * It is fine for now because counter offers are only enabled in games where there is only one current turn player.
    *  * This should be replaced by this.state.counterOfferInResponseToPlayerColor to enable counter offers in games with multiple current turn players.
    */ get tradeExecutorColor() {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined;
        if (isCounterOffer) return this.currentTurnPlayerColors[0];
        return this.state.creator;
    }
    getGivingResources(player) {
        const isCounterOfferByNonTradeExecutor = this.isCounterOfferByNonTradeExecutor();
        if (this.isTradeExecutor(player)) return isCounterOfferByNonTradeExecutor ? this.state.wantedResources : this.state.offeredResources;
        return isCounterOfferByNonTradeExecutor ? this.state.offeredResources : this.state.wantedResources;
    }
    getReceivingResources(player) {
        const isCounterOfferByNonTradeExecutor = this.isCounterOfferByNonTradeExecutor();
        if (this.isTradeExecutor(player)) return isCounterOfferByNonTradeExecutor ? this.state.offeredResources : this.state.wantedResources;
        return isCounterOfferByNonTradeExecutor ? this.state.wantedResources : this.state.offeredResources;
    }
    getRecipientColor(player) {
        if (this.isTradeExecutor(player)) return PlayerColor.None;
        return this.tradeExecutorColor;
    }
    getPlayerResponseStates() {
        const playerResponsesStates = [];
        for(const playerColor in this.state.playerResponses){
            playerResponsesStates.push({
                player: Number(playerColor),
                responseStatus: this.state.playerResponses[playerColor]
            });
        }
        return playerResponsesStates;
    }
    getPlayerResponseState(player) {
        const playerResponse = this.state.playerResponses[player];
        if (playerResponse == undefined) return undefined;
        return {
            player: Number(player),
            responseStatus: playerResponse
        };
    }
    getPlayersCreatingCounterOffer() {
        const oldPlayersCreatingCounterOffer = [];
        for(const playerColor in this.state.playersCreatingCounterOffer){
            oldPlayersCreatingCounterOffer.push({
                player: Number(playerColor),
                isCreatingCounterOffer: this.state.playersCreatingCounterOffer[playerColor]
            });
        }
        return oldPlayersCreatingCounterOffer;
    }
    getExecuteTradeActionStatus(player) {
        const playerStateController = this.playerStates.get(player);
        if (playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        if (!this.isTradeExecutor(player)) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        if (this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.cannotExecuteOpenEndedTrade'
                }
            };
        }
        const requiredResources = this.getTradeExecutorOfferedResources();
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources);
        if (!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.invalidResources'
                }
            };
        }
        return {
            canTakeAction: true,
            displayActionButton: true
        };
    }
    getAcceptTradeActionStatus(player) {
        if (this.isTradeExecutor(player)) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        const playerStateController = this.playerStates.get(player);
        if (playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        if (this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player);
        if (doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.playerBlockedTradingWithYou',
                    options: {
                        playerUsername: this.getTradeExecutorPlayerUsername()
                    }
                }
            };
        }
        const requiredResources = this.getTradeExecutorWantedResources();
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources);
        if (!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.invalidResources'
                }
            };
        }
        return {
            canTakeAction: true,
            displayActionButton: true
        };
    }
    isPlayerUnableToAcceptTrade(player) {
        if (player == PlayerColor.None) return false;
        const isTradeExecutor = this.isTradeExecutor(player);
        return isTradeExecutor ? this.isTradeExecutorUnableToAcceptTrade(player) : this.isOtherPlayerUnableToAcceptTrade(player);
    }
    getTradeAutoRejectionTime(gameSpeed, playerColor) {
        const doIHaveNoCardsToTrade = this.doesPlayerHaveAnyCardToTrade(playerColor) == false;
        if (doIHaveNoCardsToTrade) return 0;
        const defaultTimeToAutoReject = GameTimerUtils.getDefaultTradeAutoRejectionTimeByGameSpeed(gameSpeed);
        const canPlayerAccept = this.isPlayerUnableToAcceptTrade(playerColor) == false;
        if (canPlayerAccept) return defaultTimeToAutoReject;
        const adjustmentFactor = 0.75;
        const randomizationRange = TimeUtils.MILLISECONDS;
        const timeToAutoRejectIfUnableToAccept = defaultTimeToAutoReject * adjustmentFactor;
        const randomizedAutoRejectionTime = RandomUtils.randomNumberBetweenFloat(timeToAutoRejectIfUnableToAccept - randomizationRange, timeToAutoRejectIfUnableToAccept + randomizationRange);
        return randomizedAutoRejectionTime;
    }
    isTradeExecutor(player) {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined;
        if (!isCounterOffer) {
            return this.state.creator == player;
        }
        const isMyTurn = this.currentTurnPlayerColors.includes(player);
        return isMyTurn;
    }
    doesPlayerHaveAnyCardToTrade(player) {
        const playerStateController = this.playerStates.get(player);
        if (playerStateController == undefined) return true;
        return playerStateController.resourceCards.getNumberOfCardsInInventory() > 0;
    }
    isTradeExecutorUnableToAcceptTrade(player) {
        if (this.isOpenEndedTrade()) return false;
        if (this.getExecuteTradeActionStatus(player).canTakeAction) return false;
        return true;
    }
    isOtherPlayerUnableToAcceptTrade(player) {
        if (this.isOpenEndedTrade()) return this.shouldOtherPlayerFastAutoRejectOpenEndedTrade(player);
        if (this.getAcceptTradeActionStatus(player).canTakeAction) return false;
        return true;
    }
    shouldOtherPlayerFastAutoRejectOpenEndedTrade(player) {
        const playerStateController = this.playerStates.get(player);
        if (playerStateController == undefined) return false;
        const requiredResources = this.getTradeExecutorWantedResources();
        if (requiredResources.length > playerStateController.resourceCards.getNumberOfCardsInInventory()) return true;
        const numberOfRequiredAnyCards = ArrayUtils.countOccurenceOfValueInArray(requiredResources, CardEnum.AnyResource);
        const areAllWantedCardsWildCards = numberOfRequiredAnyCards == requiredResources.length;
        if (areAllWantedCardsWildCards) return false;
        const requiredResourcesWithoutAnyCard = ArrayUtils.removeAllOccurrenceFromArray(requiredResources, CardEnum.AnyResource);
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResourcesWithoutAnyCard);
        if (!doesPlayerHaveRequiredResources) return true;
        return false;
    }
    getProposeCounterOfferActionStatus(player) {
        if (player === PlayerColor.None) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player);
        if (!this.counterOffersEnabled) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        if (this.state.creator == player) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        if (doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.playerBlockedTradingWithYou',
                    options: {
                        playerUsername: this.getTradeExecutorPlayerUsername()
                    }
                }
            };
        }
        return {
            canTakeAction: true,
            displayActionButton: true
        };
    }
    getRejectTradeActionStatus(player) {
        if (player === PlayerColor.None) {
            return {
                canTakeAction: false,
                displayActionButton: false
            };
        }
        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player);
        if (doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {
                    key: 'strings:game.errors.tradeErrors.playerBlockedTradingWithYou',
                    options: {
                        playerUsername: this.getTradeExecutorPlayerUsername()
                    }
                }
            };
        }
        return {
            canTakeAction: true,
            displayActionButton: true
        };
    }
    getOfferComponentsToDisplay(player) {
        if (this.isOpenEndedTrade()) return TradeOfferOpponentDisplay.AvatarContainer;
        const isSpectator = player == PlayerColor.None;
        if (isSpectator) return TradeOfferOpponentDisplay.AvatarContainer;
        if (this.isTradeExecutor(player)) return TradeOfferOpponentDisplay.ResponseButtons;
        return TradeOfferOpponentDisplay.AvatarContainer;
    }
    isOpenEndedTrade() {
        return TradeUtils.isOpenEndedTrade(this.state.offeredResources, this.state.wantedResources);
    }
    getTradeExecutorOfferedResources() {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined;
        return isCounterOffer ? this.state.wantedResources : this.state.offeredResources;
    }
    getTradeExecutorWantedResources() {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined;
        return isCounterOffer ? this.state.offeredResources : this.state.wantedResources;
    }
    getTradeExecutorPlayerUsername() {
        const currentTurnPlayerState = this.playerStates.get(this.tradeExecutorColor);
        var _currentTurnPlayerState_username;
        return (_currentTurnPlayerState_username = currentTurnPlayerState === null || currentTurnPlayerState === void 0 ? void 0 : currentTurnPlayerState.username) !== null && _currentTurnPlayerState_username !== void 0 ? _currentTurnPlayerState_username : '';
    }
    isCounterOfferByNonTradeExecutor() {
        if (this.state.counterOfferInResponseToTradeId == undefined) return false;
        return !this.isTradeExecutor(this.state.creator);
    }
    disableCounterOffers() {
        this.counterOffersEnabled = false;
    }
    constructor(state, currentTurnPlayerColors, playerStates, embargoState){
        this.state = state;
        this.currentTurnPlayerColors = currentTurnPlayerColors;
        this.playerStates = new Map();
        for (const playerState of playerStates){
            this.playerStates.set(playerState.color, playerState);
        }
        this.embargoState = embargoState;
        this.counterOffersEnabled = true;
    }
}
